import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { cropNumber } from "../../../../common/helpers/helpers";
import DbnsfpCitations from "./DbnsfpCitations";
import {
  gerpUrl,
  dbnsfpUrl,
  PhyloP100wayUrl,
  PhyloP17wayUrl,
} from "./ConservationScoresUrls";
import {
  checkIfResultIsEmpty,
  calculateMedian,
  calculateMean,
  calculateMin,
  calculateMax,
  renderMultipleCommasWithDescription,
  getColor,
} from "./ConservationScoresHelpers";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import {
  getGerp,
  getDbnsfp,
  getPhastCons100Way,
  getPhyloP100Way,
  getSchemaProperties,
} from "../../../../state/selectors/ConservationScores.selector";
import {
  getIfVariantResultExists,
  getInSilicoThresholds,
  getNewInSilicoThresholds,
} from "../../../../state/selectors/general.selectors";
import { DBNSFP_DATABASES } from "./constants";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import { transformThresholds } from "./helpers/conservationsHelpers";
import HelperText from "../../../../components/atoms/HelperText";

class ConservationScores extends React.Component {
  constructor() {
    super();
    this.state = {};
  }

  getDbnsfpVersion = (dbnsfp) => {
    return (
      <small>
        <i>
          <a href={dbnsfpUrl} target="_blank">
            {" dbNSFP "}
          </a>
          {"version "}
          {dbnsfp[0].version}
        </i>
      </small>
    );
  };

  getPhastCons100WayRow = (phastcons100way, schema, inSilicoThresholds) => {
    const thresholds = inSilicoThresholds || "";
    const phast100cons = Array.isArray(phastcons100way)
      ? phastcons100way[0]
      : "";
    const phastconsVersion = phastcons100way && phastcons100way[0].version;
    let phastcons100Row;
    if (phast100cons) {
      phastcons100Row = (
        <tr className="dbnsfp-row">
          <td>
            <a href={PhyloP17wayUrl} target="_blank">
              {" PhastCons100way "}
            </a>
            <HelperText fieldDescriptionKey="PhastCons100way" />
            <br />
            <small>
              <i>
                {"version "}
                {phastconsVersion}
              </i>
            </small>
          </td>
          {renderMultipleCommasWithDescription(
            phast100cons,
            "conservation_score",
            schema.phastcons100way.items.properties.conservation_score.label,
            true,
            "",
            false,
            true,
            thresholds,
            "phastcons100way_vertebrate"
          )}
          <td />
          <td />
          <td />
        </tr>
      );
    }
    return phastcons100Row;
  };

  getPhyloP100WayRow = (phylop100way, schema, inSilicoThresholds) => {
    const thresholds = inSilicoThresholds || "";
    const phylop100 = Array.isArray(phylop100way) ? phylop100way[0] : "";
    const phylop100Version = phylop100way && phylop100way[0].version;
    let phylop100Row;
    if (phylop100) {
      phylop100Row = (
        <tr className="dbnsfp-row">
          <td>
            <a href={PhyloP100wayUrl} target="_blank">
              {" PhyloP100way "}
            </a>
            <HelperText fieldDescriptionKey="PhyloP100way" />
            <br />
            <small>
              <i>
                {"version "}
                {phylop100Version}
              </i>
            </small>
          </td>
          {renderMultipleCommasWithDescription(
            phylop100,
            "conservation_score",
            schema.phylop100way.items.properties.conservation_score.label,
            true,
            null,
            false,
            true,
            thresholds,
            "phylop"
          )}
          <td />
          <td />
          <td />
        </tr>
      );
    }
    return phylop100Row;
  };

  getGerpRows = (gerpRes, schema, inSilicoThresholds) => {
    const thresholds = inSilicoThresholds || "";
    const gerp = Array.isArray(gerpRes) ? gerpRes[0] : {};
    const nr = gerp.gerp_nr;
    const rs = gerp.gerp_rs;

    const nrIsMultiple = Array.isArray(nr) && nr.length > 1;
    const rsIsMultiple = Array.isArray(rs) && rs.length > 1;

    let gerpRow;
    let gerpRow1;
    let gerpRow2;

    if (nr && rs) {
      if (!nrIsMultiple && !rsIsMultiple) {
        gerpRow = (
          <tr className="dbnsfp-row">
            <td>
              <a href={gerpUrl} target="_blank">
                {schema.gerp.items.properties.gerp_nr.label.split(" ")[0]}
              </a>
              {"  "}
              <HelperText fieldDescriptionKey="GERP" />
              <br />
              {gerpRes && (
                <small>
                  <i>
                    {" version "}
                    {gerpRes[0].version}
                  </i>
                </small>
              )}
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                {schema.gerp.items.properties.gerp_nr.label
                  .split(" ")
                  .slice(1)
                  .join(" ")}
                <HelperText fieldDescriptionKey="GERP NR" />
              </div>
              <div
                className="varsome-horiz-list-value"
                style={{ color: getColor("gerp", nr, thresholds) }}
              >
                {cropNumber(nr)}
              </div>
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                {schema.gerp.items.properties.gerp_rs.label
                  .split(" ")
                  .slice(1)
                  .join(" ")}
                <HelperText fieldDescriptionKey="GERP RS" />
              </div>
              <div
                className="varsome-horiz-list-value"
                style={{ color: getColor("gerp", rs, thresholds) }}
              >
                {cropNumber(rs)}
              </div>
            </td>
            <td />
            <td />
          </tr>
        );
      } else {
        gerpRow1 = (
          <tr className="dbnsfp-row">
            <td>
              {schema.gerp.items.properties.gerp_nr.label}
              <HelperText fieldDescriptionKey="GERP NR" />
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Mean
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMean(nr))}
              </div>
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Median
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMedian(nr))}
              </div>
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Min
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMin(nr))}
              </div>
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Max
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMax(nr))}
              </div>
            </td>
          </tr>
        );
        gerpRow2 = (
          <tr className="dbnsfp-row">
            <td>
              {schema.gerp.items.properties.gerp_rs.label}
              <HelperText fieldDescriptionKey="GERP RS" />
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Mean
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMean(rs))}
              </div>
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Median
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMedian(rs))}
              </div>
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Min
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMin(rs))}
              </div>
            </td>
            <td>
              <div className="varsome-horiz-list-label varsome-dbnsfp-label">
                Max
              </div>
              <div className="varsome-horiz-list-value">
                {cropNumber(calculateMax(rs))}
              </div>
            </td>
          </tr>
        );
        gerpRow = (
          <React.Fragment>
            {gerpRow1}
            {gerpRow2}
          </React.Fragment>
        );
      }
    }
    return gerpRow;
  };

  getDbnsfpDatabaseRow = (dbnsfpRes, schema) => {
    const dbnsfp = Array.isArray(dbnsfpRes) ? dbnsfpRes[0] : {};
    const version = dbnsfpRes ? this.getDbnsfpVersion(dbnsfpRes) : null;

    const schemaProps = schema.dbnsfp.items.properties;
    const v = DBNSFP_DATABASES.map((item) => {
      if (dbnsfp[item.a] || dbnsfp[item.b] || dbnsfp[item.c]) {
        return (
          <tr className="dbnsfp-row" key={item.a}>
            <td>
              <a href={item.url} target="_blank">
                {item.name}
              </a>
              <HelperText fieldDescriptionKey={item.helpTextName} />
              <br />
              {version}
            </td>
            {renderMultipleCommasWithDescription(
              dbnsfp,
              item.a,
              schemaProps[item.a].label,
              true
            )}
            {renderMultipleCommasWithDescription(
              dbnsfp,
              item.b,
              schemaProps[item.b].label,
              true
            )}
            {item.c ? (
              renderMultipleCommasWithDescription(
                dbnsfp,
                item.c,
                schemaProps[item.c].label,
                true
              )
            ) : (
              <td />
            )}
            <td />
          </tr>
        );
      }
      return null;
    });
    return v;
  };

  render() {
    const {
      dbnsfp,
      gerp,
      phylop100,
      phastcons100,
      schemaProperties: schema,
      variantResult,
      inSilicoThresholds,
    } = this.props;

    if (schema && variantResult) {
      let content;
      if (dbnsfp || gerp || phylop100 || phastcons100) {
        let dbnsfpHasResults = false;
        let dbnsfpRows;
        if (dbnsfp) {
          dbnsfpHasResults = checkIfResultIsEmpty(dbnsfp[0]);
        }
        if (dbnsfpHasResults) {
          dbnsfpRows = this.getDbnsfpDatabaseRow(dbnsfp, schema);
        }
        const phastcons100Row = this.getPhastCons100WayRow(
          phastcons100,
          schema,
          inSilicoThresholds
        );
        const phylop100sRow = this.getPhyloP100WayRow(
          phylop100,
          schema,
          inSilicoThresholds
        );
        const gerpRow = this.getGerpRows(gerp, schema, inSilicoThresholds);

        content = (
          <div className="varsome-inner-container">
            <table className="varsome-table">
              <tbody>
                {phastcons100Row}
                {phylop100sRow}
                {gerpRow}
                {dbnsfpRows}
              </tbody>
            </table>
            <br />
            {dbnsfp && <DbnsfpCitations />}
          </div>
        );
      } else {
        content = (
          <div className="varsome-inner-container">
            No data available for this variant in GERP, PhastCons100way,
            PhyloP100way and dbNSFP.
          </div>
        );
      }
      return (
        <div className="varsome-result-fixed">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel content={content} title="Conservation Scores" />
          </div>
        </div>
      );
    }
    return null;
  }
}

ConservationScores.propTypes = {
  dbnsfp: PropTypes.arrayOf(PropTypes.shape({})),
  gerp: PropTypes.arrayOf(PropTypes.shape({})),
  phylop100: PropTypes.arrayOf(PropTypes.shape({})),
  phastcons100: PropTypes.arrayOf(PropTypes.shape({})),
  schemaProperties: PropTypes.shape({}),
  variantResult: PropTypes.bool,
  inSilicoThresholds: PropTypes.shape({}),
};

ConservationScores.defaultProps = {
  dbnsfp: null,
  gerp: null,
  phylop100: null,
  phastcons100: null,
  schemaProperties: {},
  variantResult: false,
  inSilicoThresholds: null,
};

const mapStateToProps = (state) => {
  return {
    dbnsfp: getDbnsfp(state),
    gerp: getGerp(state),
    phylop100: getPhyloP100Way(state),
    phastcons100: getPhastCons100Way(state),
    schemaProperties: getSchemaProperties(state),
    variantResult: getIfVariantResultExists(state),
    inSilicoThresholds: transformThresholds(
      getInSilicoThresholds(state) || getNewInSilicoThresholds(state)
    ),
  };
};

export default connect(
  mapStateToProps,
  {}
)(
  withErrorGuard(
    ConservationScores,
    "Conservation scores",
    "Conservation scores"
  )
);
